import { Injectable } from '@angular/core';
import { NgxsOnChanges, NgxsSimpleChange, State } from '@ngxs/store';
import { OrderModel } from 'src/app/_models/orders/order.model';
import { OrdersFiltersModel, OrderSortBy } from 'src/app/_models/self-ship/orders/orders-filters.model';
import OrdersFiltersState from './orders-filters/orders-filters.state';
import * as _ from 'lodash';
import Product from 'src/app/_entities/products/product.entity';
import Order from 'src/app/_entities/orders/order.entity';
import OrderItem from 'src/app/_entities/orders/order-item.entity';
import { HubClientMethods } from 'src/app/_services/real-time-connection/real-time-connection.constants';
import { SortOrder } from 'src/app/_enums/general/sort-order.enum';
import { RealTimeService } from 'src/app/_services/real-time-connection/real-time.service';
import { OrderBoxModel } from 'src/app/_models/orders/order-boxes/order-box.model';
import { OrderBoxItemModel } from 'src/app/_models/orders/order-boxes/order-box-item.model';
import { OrdersService } from 'src/app/_services/orders.service';
import { ProcessingStatus } from 'src/app/_entities/orders/processing-statuses/processing-status';
import PickListItem from 'src/app/_entities/pick-lists/pick-list-item.entity';
import OrderBox from '../../_entities/orders/order-boxes/order-box.entity';
import { Computed, DataAction, StateRepository } from '@angular-ru/ngxs/decorators';
import { NgxsDataEntityCollectionsRepository } from '@angular-ru/ngxs/repositories';
import { createEntityCollections } from '@angular-ru/cdk/entity';
import EntityMap from '../../_builders/entity-map';
import StoresEntitiesState from '../stores/stores-entities.state';
import EntityMapper from '../../_builders/entity.mapper';
import IEntityChange from '../../_interfaces/i-entity-change';
import { tap, shareReplay, finalize } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { StatesHelper } from '../states.helper';
import { ActivatedRoute } from '@angular/router';
import { forkJoin } from 'rxjs';
import { OrderCount } from 'src/app/_services/self-ship/self-ship-orders.service';

export type DisplayedPicklistItem = Partial<PickListItem> & { product: Product, warehouseId: number; };

export interface OrderStateModel extends OrderCount {
    entities: { [id: string]: OrderModel; };
    ids: number[];
    loading: boolean;
}

export const defaultOrderCount: OrderCount = {
    total: 0,
    open: 0,
    picked: 0,
    packed: 0,
    partiallyShipped: 0,
    shipped: 0
};

@StateRepository()
@State<OrderStateModel>({
    name: 'orders',
    defaults: {
        ...createEntityCollections(),
        ...defaultOrderCount,
        loading: false
    }
})
@Injectable()
export default class OrdersEntitiesState
    extends NgxsDataEntityCollectionsRepository<OrderModel>
    implements NgxsOnChanges {

    private _changeObserver: typeof this.state$;

    constructor(
        private realtimeService: RealTimeService,
        private ordersService: OrdersService,
        private storeEntitiesState: StoresEntitiesState,
        private ordersFiltersState: OrdersFiltersState,
        private route: ActivatedRoute
    ) {
        super();
    }

    ngxsOnChanges(_?: NgxsSimpleChange) {
        super.ngxsOnChanges(_);
        if (this.isInitialised && !this._changeObserver) {
            this._changeObserver = this.state$;
            this._changeObserver.subscribe(() => this._ngxsOnChange(this));
        }
    }

    @Computed()
    public get isLoading(): boolean {
        return this.snapshot.loading && !this.snapshot.loaded;
    }

    @Computed()
    public get orderEntities(): Order[] {
        return this.entitiesArray
            .map(order => new Order(order));
    }

    @Computed()
    public get filteredReadyToShipPercentage(): number {
        if (!this.filteredEntities.length) {
            return 0;
        }
        const ordersReadyToShip = this.filteredEntities
            .filter(order => !!(order.latestProcessingStatus.value & ProcessingStatus.ReadyToShip.value));
        return Math.round((ordersReadyToShip.length * 100) / this.filteredEntities.length);
    }

    @Computed()
    public get filteredEntities(): Order[] {
        return this._filterOrderEntities(this.orderEntities, this.ordersFiltersState.snapshot);
    }

    @Computed()
    public get filteredEntityMap(): EntityMap<Order> {
        return new EntityMap<Order>(
            EntityMapper.mapById(this._filterOrderEntities(this.orderEntities, this.ordersFiltersState.snapshot))
        );
    }

    @Computed()
    public get selectedEntities() {
        const selectedIds = Object.keys(this.ordersFiltersState.snapshot.selected);
        return selectedIds.map(id => new Order(this.entities[+id]));
    }

    @Computed()
    public get isAllSelected(): boolean {
        return Object.keys(this.ordersFiltersState.snapshot.selected).length === this.filteredEntities.length;
    }

    @Computed()
    public get ttlItemQtyPacked() {
        return (orderId: number, orderItemId: number) => {
            const order = this.entities[orderId];
            const boxes = order?.boxes ?? [];
            const boxItems = boxes.flatMap(box => box.items ?? []);
            return boxItems
                .filter(item => item?.orderItemId === orderItemId)
                .reduce((results, boxItem) => results + (boxItem?.quantity ?? 0), 0);
        };
    }

    @Computed()
    public get percentageOfItemQtyInBoxes() {
        return (orderId: number, orderItemId: number) => {
            const order = this.entities[orderId];
            const boxes = order?.boxes ?? [];
            const boxItems = boxes.flatMap(box => box.items ?? []);
            const totalOfItemQtyInBoxes = boxItems
                .filter(item => item?.orderItemId === orderItemId)
                .reduce((results, boxItem) => results + (boxItem?.quantity ?? 0), 0);

            const qtyOrdered = order?.items?.find(item => item.id === orderItemId)?.quantityOrdered ?? 0;
            return qtyOrdered ? Math.round((100 / qtyOrdered) * totalOfItemQtyInBoxes) : 0;
        };
    }

    @Computed()
    public get availableQuantityToPack() {
        return (orderId: number, orderItemId: number) => {
            const order = this.entities[orderId];
            const boxes = order?.boxes ?? [];
            const boxItems = boxes.flatMap(box => box.items ?? []);
            const totalOfItemQtyInBoxes = boxItems
                .filter(item => item?.orderItemId === orderItemId)
                .reduce((results, boxItem) => results + (boxItem?.quantity ?? 0), 0);

            const qtyOrdered = order?.items?.find(item => item.id === orderItemId)?.quantityOrdered ?? 0;
            return qtyOrdered - totalOfItemQtyInBoxes;
        };
    }

    @Computed()
    public get boxesById() {
        return (...boxIds: number[]) => {
            return new EntityMap<OrderBox>(
                EntityMapper.mapById(
                    this.entitiesArray
                        .flatMap(order => order.boxes ?? [])
                        .filter(box => boxIds.includes(box.id))
                        .map(box => new OrderBox(box))
                )
            );
        };
    }

    @Computed()
    public get itemEntitiesNotInBox() {
        return (orderId: number) => {
            const order = this.entities[orderId];
            const orderItemIdsOfBoxes = (order?.boxes ?? [])
                .flatMap(ob => ob.items ?? [])
                .map(item => item?.orderItemId)
                .filter(id => id != null);
            return (order?.items ?? [])
                .filter(item => !orderItemIdsOfBoxes.includes(item.id))
                .map(item => new OrderItem(item));
        };
    }

    @Computed()
    public get itemEntitiesNotFullyPacked() {
        return (orderId: number) => {
            const order = new Order(this.entities[orderId]);
            return order.orderItemEntities
                .filter(oi => !oi.isPacked);
        };
    }

    @Computed()
    public get storeIdToOpenOrdersMap() {
        const storeIds = this.storeEntitiesState.ids;
        let orders = this.orderEntities;
        return new EntityMap<Order[]>(
            storeIds.reduce((results, id) => {
                // results[id] = Utils.filterInPlace(orders, (order) => order.storeId === id && order.isOpen);
                /* TODO: Need to figure out how to filter orders in place to reduce the cost of the iteration - here and everywhere else */
                results[id] = orders.filter((order) => order.storeId === id && order.isOpen);
                return results;
            }, {})
        );
    }

    @DataAction()
    public reloadAllOrders() {
        this.loadCountThenOrders();
    }

    @DataAction()
    public loadCountThenOrders() {
        this.patchState({ loading: true });
        this.ordersService.getTotalOrdersCount(this.ordersService.getOrdersFetchFilterParams()).subscribe((orderCount) => {
            const batchSize = 500;
            const apiObservables = [];
            const newFetchParams = this.ordersService.getOrdersFetchFilterParams();
            this.patchState({ ...orderCount });

            for (let i = 0; i < Math.ceil(orderCount.total / batchSize); i++) {
                apiObservables.push(this.ordersService.getOrders({ ...newFetchParams }, i + 1, batchSize));
            }

            if (apiObservables.length) {
                forkJoin(apiObservables).subscribe((result: Array<OrderModel[]>) => {
                    const orders = result.flatMap(arr => arr);
                    const carrierOptions = [...new Set(orders.map(order => order.shippingMethod).filter(o => !!o))];
                    const tagOptions = [...new Set(orders.flatMap(order => order.tags).filter(o => !!o))];
                    this.ordersFiltersState.patchState({ tagOptions });
                    this.ordersFiltersState.patchState({ carrierOptions });
                    this._filterOrderEntities();
                    this.setEntitiesAll(orders);
                    this.patchState({ loading: false, loaded: true });
                });
            } else {
                this.setEntitiesAll([]);
                this.patchState({ loading: false });
            }

            this.realtimeService.registerHandler(HubClientMethods.SyncOrder, (notification: IEntityChange<OrderModel>) => {
                this.syncOrder(notification);
            });
        });
    }

    @DataAction()
    lazyLoad() {
        !this.snapshot.loading &&
            !this.snapshot.loaded &&
            this.loadCountThenOrders();
    }

    private _pendingFetches: { [id: number]: Observable<OrderModel> } = {};

    @DataAction()
    public fetchSingleOrder(orderId: number, forceReload: boolean = false): Observable<OrderModel> {
        const existing = this.entities[orderId];
         if (existing && !forceReload) {
            return of(existing);
        }

        if (!this._pendingFetches[orderId] || forceReload) {
            this._pendingFetches[orderId] = this.ordersService
                .fetchSingleOrder(orderId)
                .pipe(
                    tap(order => this.upsertOne(order)),
                    finalize(() => delete this._pendingFetches[orderId]),
                    shareReplay(1)
                );
        }

        return this._pendingFetches[orderId];
    }

    @DataAction()
    public upsertOrderBoxes(...boxes: OrderBoxModel[]) {
        const orderIdToBoxesMap = _.groupBy(boxes, (box => box.orderId));
        this.updateEntitiesMany(Object.keys(orderIdToBoxesMap).map(orderId => ({
            id: orderId,
            changes: {
                boxes: (this.entities[orderId]?.boxes ?? [])
                    .filter(box => !((orderIdToBoxesMap[orderId] ?? []).map(box => box.id).includes(box.id)))
                    .concat(orderIdToBoxesMap[orderId] ?? [])
            }
        })));
    }

    @DataAction()
    public deleteOrderBoxes(...orderBoxes: OrderBoxModel[]) {
        this.updateEntitiesMany(orderBoxes.map(ob => ({
            id: ob.orderId,
            changes: {
                ...this.entities[ob.orderId],
                boxes: (this.entities[ob.orderId]?.boxes ?? [])
                    .filter(box => box.id !== ob.id)
            }
        })));
    }

    @DataAction()
    packItemToBox(orderBoxItem: OrderBoxItemModel) {
        if (!orderBoxItem.orderId) {
            throw new Error((`[${this.constructor.name}: ${this.packItemToBox.name}]: Cannot insert an item with invalid orderId`));
        }

        this.updateOne({
            id: orderBoxItem.orderId,
            changes: {
                ...this.entities[orderBoxItem.orderId],
                boxes: (this.entities[orderBoxItem.orderId]?.boxes ?? [])
                    .map(box => {
                        if (box.id !== orderBoxItem.orderBoxId) return box;
                        const boxClone = { ...box };
                        const currentItems = box.items ?? [];
                        boxClone.items = currentItems
                            .filter(bi => bi.id !== orderBoxItem.id)
                            .concat(orderBoxItem.quantity === 0 && !orderBoxItem.assignedQuantity ? [] : [orderBoxItem]);
                        return boxClone;
                    })
            }
        });
    }

    public onStateChange(callback: (state?: OrdersEntitiesState) => void) {
        if (!this.snapshot.loading) {
            callback(this);
        }
        this._ngxsOnChange = callback;
    }

    @DataAction()
    private syncOrder(notification: IEntityChange<OrderModel>) {
        const order = notification.data;
        if (!order) {
            this.removeOne(notification.id);
        } else {
            this.upsertOne(order);
        }
    }

    private searchStart(string: string) {
        const searchTerm = StatesHelper.ordersFiltersState.snapshot.searchTerm.trim();
        return new RegExp(`(^|\\s)${searchTerm}`, 'gmi').test(string?.trim());
    }

    private _filterOrderEntities(orders: Order[] = this.orderEntities, filters: OrdersFiltersModel = this.ordersFiltersState.snapshot): Order[] {
        if (!orders.length) return [];
        const searchStart = this.searchStart;
        const customerName = this.route.snapshot.queryParams['customer'];

        return orders.filter(order => {
            // Store filter
            return (!filters.storeIds?.length || !!filters.storeIds.includes(order.storeId))
                // Warehouse filter
                && (!filters.warehouseIds?.length ||
                    filters.warehouseIds.some(wi => order.orderItemEntities.map(oi => oi.warehouseId).includes(+wi))
                )
                // Tag filter
                && (!filters.selectedTags?.length ||
                    filters.selectedTags.some(tag => order.tags.includes(tag))
                )
                // Carrier filter
                && (!filters.selectedCarrierIds?.length ||
                    filters.selectedCarrierIds.includes(order.shippingMethod)
                )
                // Date range filter
                && (new Date(order.orderDate) >= new Date(filters.dateRange.fromDate) && new Date(order.orderDate) <= new Date(filters.dateRange.toDate))
                // Processing status filter
                && (filters.orderStatuses.some(status => status?.value & order.latestProcessingStatus?.value) || !filters.orderStatuses.length)
                // Search filters
                && (
                    searchStart(order.orderNumber)
                    || searchStart(order.orderAddress?.name)
                    || order.orderItemEntities.some(item => searchStart(item.productEntity?.sku))
                    || order.orderItemEntities.some(item => searchStart(item.productEntity?.upc))
                    || order.orderItemEntities.some(item => searchStart(item.productEntity.storeProduct(order.storeId)?.asin))
                ) && (
                    // customer filter
                    !customerName || order.orderAddress?.name === customerName
                );
            // Sort filters
        }).sort(this.getSortByLambda(filters));
    }

    private getSortByLambda(filters: OrdersFiltersModel) {
        switch (filters.sortBy) {
            case OrderSortBy['Customer_Name']:
                return (a: Order, b: Order) => a.orderAddress?.name > b.orderAddress?.name
                    ? (filters.sortOrder === SortOrder.Asc ? 1 : -1) : (filters.sortOrder === SortOrder.Asc ? -1 : 1);
            case OrderSortBy['Order_Date']:
                return (a: Order, b: Order) => a.orderDate < b.orderDate
                    ? (filters.sortOrder === SortOrder.Asc ? 1 : -1) : (filters.sortOrder === SortOrder.Asc ? -1 : 1);
            case OrderSortBy.Status:
                return (a: Order, b: Order) => a.latestProcessingStatus.value < b.latestProcessingStatus.value
                    ? (filters.sortOrder === SortOrder.Asc ? 1 : -1) : (filters.sortOrder === SortOrder.Asc ? -1 : 1);
            case OrderSortBy.Store:
                return (a: Order, b: Order) => a.store?.type < b.store?.type
                    ? (filters.sortOrder === SortOrder.Asc ? 1 : -1) : (filters.sortOrder === SortOrder.Asc ? -1 : 1);
            case OrderSortBy['Total_Quantity']:
                return (a: Order, b: Order) => (a.items ?? []).reduce((a, b) => a + b.quantityOrdered, 0) > (b.items ?? []).reduce((a, b) => a + b.quantityOrdered, 0)
                    ? (filters.sortOrder === SortOrder.Asc ? 1 : -1) : (filters.sortOrder === SortOrder.Asc ? -1 : 1);
        }
    }

    private _ngxsOnChange = (state?: OrdersEntitiesState) => { };
}
